'use client';

import { Check, ChevronDownIcon, Search, X } from 'lucide-react';

import { ReactNode, useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
} from '@/components/ui/command';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

import { cn } from '@/lib/utils';

type SelectOption = { label: string; value: string };

type SelectDropdownProps = {
	options: SelectOption[] | ReadonlyArray<SelectOption>;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	searchPlaceholder?: string;
	/** Renders a search input inside the dropdown. Default: false */
	searchable?: boolean;
	/**
	 * Renders a minimal ghost trigger that auto-sizes to the selected label,
	 * matching the InlineSelect visual style.
	 */
	inline?: boolean;
	disabled?: boolean;
	className?: string;
	/** Width of the dropdown content panel. Default: "w-56" */
	contentWidth?: string;
	maxHeight?: number;
	id?: string;
	/** Optional prefix label shown beside the trigger in inline mode */
	label?: string;
	/** Max-width of the inline trigger. Default: 200 */
	maxWidth?: number;
	/** Extra classes applied to the inline trigger text spans (e.g. to override text size) */
	triggerTextClassName?: string;
	/** Extra classes applied to option labels and the pinned footer action. */
	menuItemTextClassName?: string;
	/** When provided, renders this element as the trigger instead of the default inline/button trigger */
	customTrigger?: ReactNode;
	/** Pinned action row under the options, e.g. "Create new…". Not searchable or selectable as an option. */
	footerAction?: { label: string; icon?: ReactNode; onSelect: () => void; className?: string };
};

function SelectDropdown({
	options,
	value,
	onChange,
	placeholder = 'Select...',
	searchPlaceholder = 'Search...',
	searchable = false,
	inline = false,
	disabled = false,
	className,
	contentWidth = 'w-56',
	maxHeight = 300,
	id,
	label,
	triggerTextClassName,
	menuItemTextClassName,
	maxWidth = 200,
	customTrigger,
	footerAction,
}: SelectDropdownProps) {
	const [open, setOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');

	const filteredOptions = useMemo(
		() =>
			searchable && searchTerm
				? options.filter((o) => o.label.toLowerCase().includes(searchTerm.toLowerCase()))
				: options,
		[options, searchable, searchTerm]
	);

	const selectedLabel = useMemo(
		() => options.find((o) => o.value === value)?.label ?? '',
		[options, value]
	);

	const handleSelect = useCallback(
		(optionValue: string) => {
			onChange(optionValue);
			setOpen(false);
			setSearchTerm('');
		},
		[onChange]
	);

	const handleOpenChange = useCallback((next: boolean) => {
		setOpen(next);
		if (!next) setSearchTerm('');
	}, []);

	const trigger = customTrigger ? (
		<DropdownMenuTrigger
			id={id}
			disabled={disabled}
			asChild
		>
			<span>{customTrigger}</span>
		</DropdownMenuTrigger>
	) : inline ? (
		<DropdownMenuTrigger
			id={id}
			disabled={disabled}
			className={cn(
				'flex cursor-pointer items-center gap-1 bg-transparent outline-none disabled:cursor-not-allowed disabled:opacity-50',
				className
			)}
		>
			{label && <span className="text-xs text-slate-400">{label}</span>}
			<div
				className="relative flex items-center"
				style={{ maxWidth: `${maxWidth}px` }}
			>
				{/* Invisible sizer — stretches the container to the selected label width */}
				<span
					aria-hidden
					className={cn('invisible truncate whitespace-nowrap pr-4 text-xs', triggerTextClassName)}
				>
					{selectedLabel || placeholder}
				</span>
				<span
					className={cn(
						'absolute inset-0 truncate pr-4 text-xs',
						selectedLabel ? 'text-slate-500' : 'text-slate-400',
						triggerTextClassName
					)}
				>
					{selectedLabel || placeholder}
				</span>
				<ChevronDownIcon className="pointer-events-none absolute right-0 size-3 text-slate-400" />
			</div>
		</DropdownMenuTrigger>
	) : (
		<DropdownMenuTrigger asChild>
			<Button
				id={id}
				variant="outline"
				role="combobox"
				aria-expanded={open}
				disabled={disabled}
				className={cn(
					'w-full justify-between text-left font-normal',
					!value && 'text-muted-foreground',
					className
				)}
			>
				<span className="truncate">{selectedLabel || placeholder}</span>
				<ChevronDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
			</Button>
		</DropdownMenuTrigger>
	);

	return (
		<DropdownMenu
			open={open}
			onOpenChange={handleOpenChange}
		>
			{trigger}
			<DropdownMenuContent
				className={cn('p-0', contentWidth)}
				align="start"
			>
				<Command>
					{searchable && (
						<div className="p-1 pb-0">
							<div className="relative">
								<Input
									type="text"
									placeholder={searchPlaceholder}
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									className="h-9 px-9"
								/>
								<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-secondary" />
								{searchTerm && (
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="absolute right-0 top-1/2 -translate-y-1/2 hover:bg-transparent"
										onClick={() => setSearchTerm('')}
									>
										<X className="size-4" />
										<span className="sr-only">Clear search</span>
									</Button>
								)}
							</div>
						</div>
					)}
					<CommandList>
						<ScrollArea
							className="p-1"
							style={{
								height: filteredOptions.length > 8 ? `${maxHeight}px` : 'auto',
							}}
							viewportClassName="[&>div]:!block"
						>
							<CommandEmpty>No matches</CommandEmpty>
							<CommandGroup>
								{filteredOptions.map((option) => (
									<CommandItem
										key={option.value}
										// Without this, cmdk derives the item's value from its text — two
										// options sharing a label would then highlight as one.
										value={option.value}
										onSelect={() => handleSelect(option.value)}
										className="max-w-full cursor-pointer"
									>
										<span className={cn('flex-1 truncate text-xs', menuItemTextClassName)}>
											{option.label}
										</span>
										{option.value === value && <Check className="ml-2 size-3 shrink-0" />}
									</CommandItem>
								))}
							</CommandGroup>
						</ScrollArea>
					</CommandList>
					{footerAction && (
						<div className="border-t p-1">
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className={cn(
									'h-8 w-full justify-start gap-1.5 text-xs font-normal',
									menuItemTextClassName,
									footerAction.className
								)}
								onClick={() => {
									handleOpenChange(false);
									footerAction.onSelect();
								}}
							>
								{footerAction.icon}
								{footerAction.label}
							</Button>
						</div>
					)}
				</Command>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

export { SelectDropdown, type SelectOption, type SelectDropdownProps };
